import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success("Welcome back");
            const dest = u?.role === "platform_admin" ? "/platform" : (location.state?.from?.pathname || "/portal");
            navigate(dest, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 auth-grid-bg">
            <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border surface">
                <div>
                    <div className="label-overline mb-2">FormFlo</div>
                    <div className="text-3xl font-display font-bold tracking-tight">FormFlo<span className="text-primary">.</span></div>
                </div>
                <div className="space-y-6">
                    <div className="marker-line" />
                    <h2 className="text-4xl font-display font-semibold tracking-tight leading-tight">
                        Forms that flow,<br />approvals that move.
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
                        The metadata-driven low-code platform for internal apps, intake flows, and approval workflows. Built for teams who want clarity, not complexity.
                    </p>
                    <div className="grid grid-cols-3 gap-px border border-border bg-border">
                        {["multi-tenant", "rbac + groups", "workflows"].map((t) => (
                            <div key={t} className="surface p-4">
                                <div className="label-overline">{t}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">FormFlo v1 · phase 3</div>
            </div>

            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-md brut-section p-10" data-testid="login-card">
                    <div className="label-overline mb-2">Sign in</div>
                    <h1 className="text-3xl font-bold tracking-tight mb-8">Access your workspace</h1>
                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="label-overline">Email</Label>
                            <Input
                                id="email" type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@company.com" className="mt-2 rounded-sm h-11"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="label-overline">Password</Label>
                                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">Forgot?</Link>
                            </div>
                            <Input
                                id="password" type="password" required value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••" className="mt-2 rounded-sm h-11"
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full rounded-sm h-11 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="login-submit-button">
                            {loading ? "Signing in..." : "Sign in"}
                        </Button>
                    </form>
                    <div className="mt-8 pt-6 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                            New here?{" "}
                            <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signup-link">Start a workspace</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
